import { HttpErrorResponse } from '@angular/common/http';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeature, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

import { DefaultChecklistActions } from '../actions/checklist.actions';
import { DEFAULT_CHECKLIST_FEATURE_KEY } from '../constants/constants';
import { GetDefaultChecklistDto } from '../interfaces/checklists/get-default-checklist-dto.interface';

export interface DefaultChecklistState extends EntityState<GetDefaultChecklistDto> {
    loading: boolean;
    error: HttpErrorResponse | null;
    checkListForTranslations: Partial<GetDefaultChecklistDto>[];
}

export const defaultChecklistAdapter: EntityAdapter<GetDefaultChecklistDto> =
    createEntityAdapter<GetDefaultChecklistDto>();

export const initialState: DefaultChecklistState = defaultChecklistAdapter.getInitialState({
    loading: false,
    error: null,
    checkListForTranslations: null,
});

export const selectDefaultChecklistState = createFeatureSelector<DefaultChecklistState>(DEFAULT_CHECKLIST_FEATURE_KEY);

const { selectEntities, selectAll } = defaultChecklistAdapter.getSelectors();

export const selectChecklistEntities = createSelector(selectDefaultChecklistState, selectEntities);
export const selectAllDefaultChecklists = createSelector(selectDefaultChecklistState, selectAll);

export const selectChecklistError = createSelector(
    selectDefaultChecklistState,
    (state: DefaultChecklistState) => state.error,
);

export const selectDefaultChecklistById = (id: string) =>
    createSelector(selectChecklistEntities, (entities) => entities[id]);

export const selectChecklistForTranslation = createSelector(
    selectDefaultChecklistState,
    (state) => state.checkListForTranslations,
);

export const checklistReducer = createReducer(
    initialState,
    on(DefaultChecklistActions.loadChecklists, (state) => ({ ...state, loading: true })),
    on(DefaultChecklistActions.loadChecklistsSuccess, (state, { checklists }) =>
        defaultChecklistAdapter.setAll(checklists, { ...state, loading: false }),
    ),
    on(DefaultChecklistActions.loadChecklistsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(DefaultChecklistActions.createChecklist, (state) => ({ ...state, loading: true })),
    on(DefaultChecklistActions.createChecklistSuccess, (state, { checklist }) =>
        defaultChecklistAdapter.addOne(checklist, { ...state, loading: false }),
    ),
    on(DefaultChecklistActions.createChecklistFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(DefaultChecklistActions.updateChecklist, (state) => ({ ...state, loading: true })),
    on(DefaultChecklistActions.updateChecklistSuccess, (state, { checklist }) =>
        defaultChecklistAdapter.updateOne({ id: checklist.id, changes: checklist }, { ...state, loading: false }),
    ),
    on(DefaultChecklistActions.updateChecklistFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(DefaultChecklistActions.loadChecklistById, (state) => ({ ...state, loading: true })),
    on(DefaultChecklistActions.loadChecklistByIdSuccess, (state, { checklist }) =>
        defaultChecklistAdapter.upsertOne(checklist, { ...state, loading: false }),
    ),
    on(DefaultChecklistActions.loadChecklistByIdFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    on(DefaultChecklistActions.deleteChecklists, (state) => ({ ...state, loading: true })),
    on(DefaultChecklistActions.deleteChecklistsSuccess, (state, { ids }) =>
        defaultChecklistAdapter.removeMany(ids, { ...state, loading: false }),
    ),
    on(DefaultChecklistActions.deleteChecklistsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    on(DefaultChecklistActions.getChecklistsForTranslations, (state) => ({ ...state, loading: true })),
    on(DefaultChecklistActions.getChecklistsForTranslationsSuccess, (state, { data }) => {
        return {
            ...state,
            loading: false,
            checkListForTranslations: [...data],
        };
    }),
    on(DefaultChecklistActions.getChecklistsForTranslationsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
);

export const defaultChecklistFeature = createFeature({
    name: DEFAULT_CHECKLIST_FEATURE_KEY,
    reducer: checklistReducer,
});
